/**
 * Galerie de l'album : combine les souvenirs déposés par les invités (persistés
 * dans IndexedDB) et le contenu de démonstration livré avec l'application.
 */
import { computed, ref } from "vue";
import { defineStore } from "pinia";

import type { FilterId, MediaItem, MomentId, StoredMedia } from "@/types/album";

import { DEMO_COUNT_OFFSET, DEMO_MEDIA } from "@/data/demo-media";
import { deleteMedia, getAllMedia, isQuotaError, putMedia } from "@/lib/db";
import { formatDuration } from "@/lib/format";
import { createId } from "@/lib/id";
import { detectKind } from "@/lib/validation";
import { useNotificationsStore } from "@/stores/notifications";

const DEMO_LIKES_STORAGE_KEY = "sq-album:demo-likes";
const DEMO_HIDDEN_STORAGE_KEY = "sq-album:demo-hidden";
const METADATA_TIMEOUT_MS = 3000;
/** Nombre de souvenirs préparés en parallèle au démarrage. */
const HYDRATE_BATCH_SIZE = 5;
const DEFAULT_CAPTION = "Un instant à garder";
const DEFAULT_AUTHOR = "Un invité";

const STORAGE_UNAVAILABLE_MESSAGE =
  "Le stockage local n'est pas disponible sur cet appareil : vos souvenirs resteront visibles seulement pendant cette visite.";
const QUOTA_EXCEEDED_MESSAGE =
  "Mémoire de stockage pleine : ce souvenir reste visible ici mais n'a pas pu être sauvegardé durablement.";

/** Lit un ensemble d'identifiants de démonstration persisté localement. */
function readIdSet(key: string): Set<string> {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return new Set();
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return new Set();
    return new Set(parsed.filter((value): value is string => typeof value === "string"));
  } catch {
    return new Set();
  }
}

function writeIdSet(key: string, ids: Set<string>): void {
  try {
    localStorage.setItem(key, JSON.stringify([...ids]));
  } catch {
    // Navigation privée ou quota dépassé : le réglage ne survit pas à la session.
  }
}

/** Lit la durée d'une vidéo via un élément `<video>` hors DOM, avec repli silencieux. */
function readVideoDuration(url: string): Promise<string | undefined> {
  return new Promise((resolve) => {
    const video = document.createElement("video");
    video.preload = "metadata";

    const finish = (label: string | undefined): void => {
      clearTimeout(timeoutId);
      video.removeAttribute("src");
      resolve(label);
    };

    const timeoutId = setTimeout(() => finish(undefined), METADATA_TIMEOUT_MS);
    video.onloadedmetadata = () => {
      finish(Number.isFinite(video.duration) ? formatDuration(video.duration * 1000) : undefined);
    };
    video.onerror = () => finish(undefined);
    video.src = url;
  });
}

function toStoredRecord(item: MediaItem, blob: Blob): StoredMedia {
  return {
    id: item.id,
    kind: item.kind,
    author: item.author,
    moment: item.moment,
    caption: item.caption,
    createdAt: item.createdAt,
    liked: item.liked,
    blob,
  };
}

export const useAlbumStore = defineStore("album", () => {
  const guestItems = ref<MediaItem[]>([]);
  const filter = ref<FilterId>("all");
  const isReady = ref(false);
  const isPersistent = ref(true);
  const demoLikes = ref<Set<string>>(readIdSet(DEMO_LIKES_STORAGE_KEY));
  /**
   * Souvenirs de démonstration masqués. Ils vivent dans le bundle, pas dans
   * IndexedDB : on ne peut pas les effacer, seulement retenir localement ceux
   * à ne plus afficher — sans quoi ils réapparaîtraient au rechargement.
   */
  const demoHidden = ref<Set<string>>(readIdSet(DEMO_HIDDEN_STORAGE_KEY));

  // Object URLs créés pour les médias invités, à révoquer explicitement.
  const objectUrls = new Set<string>();
  // Fichiers d'origine des médias invités, nécessaires pour repersister un like.
  const guestBlobs = new Map<string, Blob>();

  const decoratedDemoMedia = computed<MediaItem[]>(() =>
    DEMO_MEDIA.filter((item) => !demoHidden.value.has(item.id)).map((item) =>
      demoLikes.value.has(item.id) ? { ...item, liked: true } : item,
    ),
  );

  const items = computed<MediaItem[]>(() => [...guestItems.value, ...decoratedDemoMedia.value]);

  const filteredItems = computed<MediaItem[]>(() => {
    if (filter.value === "all") return items.value;
    if (filter.value === "video") return items.value.filter((item) => item.kind === "video");
    return items.value.filter((item) => item.moment === filter.value);
  });

  const totalCount = computed(() => DEMO_COUNT_OFFSET + items.value.length);

  function setFilter(id: FilterId): void {
    filter.value = id;
  }

  function indexOfId(id: string): number {
    return items.value.findIndex((item) => item.id === id);
  }

  function revokeUrl(url: string): void {
    if (objectUrls.has(url)) {
      URL.revokeObjectURL(url);
      objectUrls.delete(url);
    }
  }

  function revokeItemUrls(item: MediaItem): void {
    revokeUrl(item.previewUrl);
    if (item.playbackUrl && item.playbackUrl !== item.previewUrl) {
      revokeUrl(item.playbackUrl);
    }
  }

  async function toMediaItem(record: StoredMedia): Promise<MediaItem> {
    const url = URL.createObjectURL(record.blob);
    objectUrls.add(url);

    const base = {
      id: record.id,
      kind: record.kind,
      origin: "guest" as const,
      author: record.author,
      moment: record.moment,
      caption: record.caption,
      createdAt: record.createdAt,
      liked: record.liked,
      previewUrl: url,
    };

    if (record.kind !== "video") {
      return base;
    }

    const durationLabel = await readVideoDuration(url);
    const videoItem: MediaItem = { ...base, playbackUrl: url };
    return durationLabel === undefined ? videoItem : { ...videoItem, durationLabel };
  }

  async function hydrate(): Promise<void> {
    const notifications = useNotificationsStore();
    try {
      const stored = await getAllMedia();
      const sorted = [...stored].sort((a, b) => b.createdAt - a.createdAt);
      sorted.forEach((record) => guestBlobs.set(record.id, record.blob));

      // Traitement par lots plutôt qu'un `Promise.all` global : chaque vidéo
      // fait décoder ses métadonnées par un `<video>` hors DOM. Sur un album
      // bien rempli, tout lancer d'un coup revient à ouvrir des dizaines de
      // décodeurs simultanés et fige l'affichage initial sur un téléphone
      // d'entrée de gamme.
      const items: MediaItem[] = [];
      for (let offset = 0; offset < sorted.length; offset += HYDRATE_BATCH_SIZE) {
        const batch = sorted.slice(offset, offset + HYDRATE_BATCH_SIZE);
        items.push(...(await Promise.all(batch.map((record) => toMediaItem(record)))));
      }
      guestItems.value = items;
    } catch {
      isPersistent.value = false;
      notifications.notify(STORAGE_UNAVAILABLE_MESSAGE, "error");
    } finally {
      isReady.value = true;
    }
  }

  async function addGuestMedia(input: {
    files: File[];
    author: string;
    moment?: MomentId | undefined;
    caption: string;
  }): Promise<MediaItem[]> {
    const notifications = useNotificationsStore();
    const author = input.author.trim() || DEFAULT_AUTHOR;
    const caption = input.caption.trim() || DEFAULT_CAPTION;
    const createdItems: MediaItem[] = [];

    for (const file of input.files) {
      const kind = detectKind(file) ?? "image";
      const record: StoredMedia = {
        id: createId("guest-media"),
        kind,
        author,
        moment: input.moment,
        caption,
        createdAt: Date.now(),
        liked: false,
        blob: file,
      };
      guestBlobs.set(record.id, file);

      try {
        await putMedia(record);
      } catch (error) {
        isPersistent.value = false;
        notifications.notify(
          isQuotaError(error) ? QUOTA_EXCEEDED_MESSAGE : STORAGE_UNAVAILABLE_MESSAGE,
          "error",
        );
      }

      createdItems.push(await toMediaItem(record));
    }

    guestItems.value = [...createdItems, ...guestItems.value];
    return createdItems;
  }

  async function removeMedia(id: string): Promise<void> {
    const target = items.value.find((item) => item.id === id);
    if (!target) return;

    if (target.origin === "demo") {
      const next = new Set(demoHidden.value);
      next.add(id);
      demoHidden.value = next;
      writeIdSet(DEMO_HIDDEN_STORAGE_KEY, next);
      return;
    }

    try {
      await deleteMedia(id);
    } catch {
      // Le stockage peut être indisponible : on retire quand même l'item affiché.
    }

    guestItems.value = guestItems.value.filter((item) => item.id !== id);
    guestBlobs.delete(id);
    revokeItemUrls(target);
  }

  function toggleDemoLike(id: string): void {
    const isDemoId = DEMO_MEDIA.some((item) => item.id === id);
    if (!isDemoId) return;

    const next = new Set(demoLikes.value);
    if (next.has(id)) {
      next.delete(id);
    } else {
      next.add(id);
    }
    demoLikes.value = next;
    writeIdSet(DEMO_LIKES_STORAGE_KEY, next);
  }

  async function toggleGuestLike(index: number): Promise<void> {
    const current = guestItems.value[index];
    if (!current) return;

    const updated: MediaItem = { ...current, liked: !current.liked };
    guestItems.value = [
      ...guestItems.value.slice(0, index),
      updated,
      ...guestItems.value.slice(index + 1),
    ];

    const blob = guestBlobs.get(updated.id);
    if (!blob) return;

    const notifications = useNotificationsStore();
    try {
      await putMedia(toStoredRecord(updated, blob));
    } catch (error) {
      isPersistent.value = false;
      notifications.notify(
        isQuotaError(error) ? QUOTA_EXCEEDED_MESSAGE : STORAGE_UNAVAILABLE_MESSAGE,
        "error",
      );
    }
  }

  async function toggleLike(id: string): Promise<void> {
    const guestIndex = guestItems.value.findIndex((item) => item.id === id);
    if (guestIndex !== -1) {
      await toggleGuestLike(guestIndex);
      return;
    }
    toggleDemoLike(id);
  }

  function disposeAll(): void {
    objectUrls.forEach((url) => URL.revokeObjectURL(url));
    objectUrls.clear();
  }

  return {
    items,
    filter,
    filteredItems,
    totalCount,
    isReady,
    isPersistent,
    setFilter,
    hydrate,
    addGuestMedia,
    removeMedia,
    toggleLike,
    indexOfId,
    disposeAll,
  };
});
